import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Typography,
    Button,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    TextField,
    Switch,
    FormControlLabel,
    Box,
    Chip,
    Divider,
    MenuItem
} from "@mui/material";
import { Close as CloseIcon } from '@mui/icons-material';

// --- NEW MODAL COMPONENT ---
export const LinkMenuModal = ({ open, onClose, menuData, onLinkItemSave }) => {
    const [selectedItem, setSelectedItem] = useState(null);
    const [formValues, setFormValues] = useState({
        stockmanaged: false,
        itemsellcnt: 1,
        currentStock: 0,
        minThr: 5,
        maxThr: 0,
        pCost: 0.00,
        supplier: '',
        unit: 'unit', // Default to a safe unit
    });

    // Flatten menu data for simple dropdown selection
    const flatMenuItems = useMemo(() => {
        let items = [];
        menuData.forEach(cat => {
            // Include storeID from the category level
            const storeID = cat.storeID; 
            items = items.concat(cat.items.map(item => ({ ...item, catgory: cat.catgory, storeID: storeID })));
        });
        return items;
    }, [menuData]);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!open) {
            setSelectedItem(null);
            setFormValues({
                stockmanaged: false,
                itemsellcnt: 1,
                currentStock: 0,
                minThr: 5,
                maxThr: 0,
                pCost: 0.00,
                supplier: '',
                unit: 'unit',
            });
        } else {
             // Set default values from the selected item if available (for editing)
            if (selectedItem) {
                setFormValues(prev => ({
                    ...prev,
                    stockmanaged: selectedItem.stockmanaged || false,
                    unit: selectedItem.unit || 'unit',
                    currentStock: selectedItem.currentStock || 0,
                    minThr: selectedItem.minThr || 5,
                }));
            }
        }
    }, [open, selectedItem]);

    const handleItemChange = (event) => {
        const itemId = event.target.value;
        const item = flatMenuItems.find(i => i._id === itemId);
        if (item) {
            setSelectedItem(item);
        }
    };

    const handleFormChange = (event) => {
        const { name, value, type, checked } = event.target;
        // Convert to number if applicable, but handle empty strings first
        const inputValue = (type === 'number' && value !== '') ? parseFloat(value) : value;

        setFormValues(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : inputValue
        }));
    };

    const handleSave = () => {
        if (!selectedItem) {
            alert('Please select a menu item.');
            return;
        }

        if (formValues.stockmanaged && (!formValues.itemsellcnt || formValues.currentStock === null || formValues.minThr === null)) {
            alert('Please fill all required stock fields (Items Per Sale Unit, Current Stock, Min Threshold).');
            return;
        }

        const dataToSave = {
            productID: selectedItem._id,
            storeID: selectedItem.storeID || 'UNKNOWN_STORE', 
            ...formValues,
        };

        onLinkItemSave(dataToSave);
        onClose();
    };


    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Link Menu Item for Inventory
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Select a menu item to connect it for stock tracking and define its initial metrics.
                </Typography>

                {/* --- 1. Menu Item Selection Dropdown --- */}
                <FormControl fullWidth sx={{ mb: 3 }} size="small">
                    <InputLabel id="menu-item-select-label">Select Product</InputLabel>
                    <Select
                        labelId="menu-item-select-label"
                        value={selectedItem ? selectedItem._id : ''}
                        label="Select Product"
                        onChange={handleItemChange}
                    >
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                        {flatMenuItems.map((item) => (
                            <MenuItem 
                                key={item._id} 
                                value={item._id}
                                // Only disable if already managed AND it's not the currently selected item (for editing)
                                disabled={item.stockmanaged && item._id !== (selectedItem?._id || '')}
                            >
                                {`${item.item} (${item.catgory}) ${item.stockmanaged ? ' [LINKED]' : ''}`}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* --- 2. Item Details and Status Toggle --- */}
                {selectedItem && (
                    <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: selectedItem.stockmanaged ? '#f0fff0' : '#fff0f0' }}>
                        <Typography variant="subtitle1" fontWeight="bold">{selectedItem.item}</Typography>
                        <Typography variant="body2">Category: {selectedItem.catgory}</Typography>
                        <Typography variant="body2">Price: ${selectedItem.price.toFixed(2)}</Typography>
                        <Chip 
                            label={selectedItem.stockmanaged ? "Stock Tracking ENABLED" : "Stock Tracking DISABLED"} 
                            color={selectedItem.stockmanaged ? 'success' : 'error'} 
                            size="small" 
                            sx={{ mt: 1 }}
                        />

                        <Divider sx={{ my: 2 }} />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formValues.stockmanaged}
                                    onChange={handleFormChange}
                                    name="stockmanaged"
                                    color="primary"
                                />
                            }
                            label="Enable Stock Management"
                        />
                    </Paper>
                )}
                
                {/* --- 3. Inventory Input Fields --- */}
                {selectedItem && formValues.stockmanaged && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="h6" color="primary">Stock Configuration</Typography>

                        <FormControl fullWidth size="small" required>
                             <InputLabel>Unit of Measure</InputLabel>
                            <Select
                                name="unit"
                                value={formValues.unit}
                                label="Unit of Measure"
                                onChange={handleFormChange}
                            >
                                {['kg', 'g', 'Liters', 'ml', 'unit', 'pack'].map(unit => (
                                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Items Per Sale Unit"
                            name="itemsellcnt"
                            type="number"
                            size="small"
                            value={formValues.itemsellcnt}
                            onChange={handleFormChange}
                            helperText="How many units of this item are sold at once (e.g., 1 for a single coffee)."
                            required
                        />
                        <TextField
                            label={`Current Stock Quantity (${formValues.unit})`}
                            name="currentStock"
                            type="number"
                            size="small"
                            value={formValues.currentStock}
                            onChange={handleFormChange}
                            required
                        />
                        <TextField
                            label="Minimum Threshold (Low Stock Alert)"
                            name="minThr"
                            type="number"
                            size="small"
                            value={formValues.minThr}
                            onChange={handleFormChange}
                            required
                        />
                        <TextField
                            label="Maximum Stock (Optional)"
                            name="maxThr"
                            type="number"
                            size="small"
                            value={formValues.maxThr}
                            onChange={handleFormChange}
                        />
                        <TextField
                            label="Purchase Cost (P/Unit)"
                            name="pCost"
                            type="number"
                            size="small"
                            value={formValues.pCost}
                            onChange={handleFormChange}
                        />
                        <TextField
                            label="Primary Supplier"
                            name="supplier"
                            type="text"
                            size="small"
                            value={formValues.supplier}
                            onChange={handleFormChange}
                        />
                    </Box>
                )}

            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">
                    Cancel
                </Button>
                {selectedItem && (
                    <Button onClick={handleSave} color="primary" variant="contained" disabled={!formValues.stockmanaged}>
                        Save Link
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
